import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as PalylistFolder } from "../../../../assets/icons/svg/PlayList-image-folder.svg";
import { ReactComponent as Icon } from "../../../../assets/icons/svg/add-folder-whiteColor.svg";
import { useTopBar } from "../../../../context/TopBarContext";
import { usePlaylist } from "../../../../context/PlaylistContext";
import CustomButton from "../../../ui/CommonButton";
import ItemListPopup from "../../../ui/ItemListPopup";

// CreatePlaylistForm Component
interface CreatePlaylistFormProps {
  onClose: () => void;
  onSubmit: (playlistName: string) => void;
  isLoading?: boolean;
}

const CreatePlaylistForm: React.FC<CreatePlaylistFormProps> = ({
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [playlistName, setPlaylistName] = useState<string>("");
  const [isCreateEnabled, setIsCreateEnabled] = useState<boolean>(false);

  useEffect(() => {
    setIsCreateEnabled(playlistName.trim().length > 0 && !isLoading);
  }, [playlistName, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (playlistName.trim() && !isLoading) {
      onSubmit(playlistName.trim());
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      setPlaylistName("");
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isCreateEnabled) {
      handleSubmit(e);
    }
    if (e.key === "Escape" && !isLoading) {
      handleCancel();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1">
        <div className="mb-6">
          <label
            htmlFor="playlistName"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Playlist name
          </label>
          <input
            type="text"
            id="playlistName"
            value={playlistName}
            onChange={(e) => setPlaylistName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter playlist name"
            className="w-full px-3 py-2 border border-gray-300 rounded-[8px] focus:outline-none focus:ring-[#0BA6C7] focus:border-[#0BA6C7] text-sm disabled:bg-gray-50 disabled:text-gray-500"
            autoFocus
            disabled={isLoading}
            maxLength={100}
          />
          {playlistName.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {playlistName.length}/100 characters
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 my-4"></div>

      <div className="flex gap-3 mt-auto">
        <CustomButton
          variant="outline"
          onClick={handleCancel}
          type="button"
          className="flex-1"
          disabled={isLoading}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="default"
          isActive={isCreateEnabled}
          onClick={handleSubmit}
          disabled={!isCreateEnabled || isLoading}
          type="submit"
          className="flex-1"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Creating...
            </div>
          ) : (
            "Create"
          )}
        </CustomButton>
      </div>
    </div>
  );
};

// Updated Main CreatePlaylistScreen Component
const CreatePlaylistScreen: React.FC = () => {
  const navigate = useNavigate();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const { addPlaylist } = usePlaylist();
  const location = useLocation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const title = "Create Playlist";
    const showBack = false;

    setTitle(title);
    setShowBackButton(showBack);

    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });

    // Check if we should auto-open the popup
    if (location.state?.autoOpenPopup) {
      setShowCreateForm(true);
    }
  }, [
    location.pathname,
    location.state,
    setTitle,
    setShowBackButton,
    pushNavState,
  ]);

  const handleOpenCreateForm = () => {
    setShowCreateForm(true);
  };

  const handleCloseCreateForm = () => {
    if (!isCreating) {
      setShowCreateForm(false);
    }
  };

  const handleCreatePlaylist = async (playlistName: string) => {
    setIsCreating(true);

    try {
      // Simulate API call delay (remove this in production)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create the playlist immediately
      const newPlaylist = addPlaylist(playlistName);

      // Close the form
      setShowCreateForm(false);

      // Navigate to DefaultEmptyStateView with the new playlist ID
      navigate(`/playlists/playlist/${newPlaylist.id}`, {
        state: {
          isNewPlaylist: true,
        },
      });
    } catch (error) {
      console.error("Error creating playlist:", error);
      // Handle error (show toast, etc.)
    } finally {
      setIsCreating(false);
    }
  };

  // Handle escape key to close form
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showCreateForm && !isCreating) {
        setShowCreateForm(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showCreateForm, isCreating]);

  return (
    <>
      {/* Background overlay when popup is open */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      )}

      <div
        className={`fixed inset-20 2xl:inset-40 overflow-hidden flex items-center justify-center bg-white ${
          showCreateForm ? "pointer-events-none opacity-50" : ""
        }`}
      >
        <div className="flex flex-col items-center text-center space-y-6 max-w-md">
          <PalylistFolder className="w-52 h-52 -mb-8" />
          <div className="space-y-3">
            <h2 className="text-2xl font-semibold text-[#1F1F1F]">
              Create Playlist
            </h2>
            <p className="text-sm text-[#808191F5] leading-relaxed">
              Playlists help you organize the order of the content that you want
              to show. It's as easy as drag-and-drop.
            </p>
          </div>
          <button
            onClick={handleOpenCreateForm}
            disabled={isCreating || showCreateForm}
            className="inline-flex items-center justify-center gap-2.5 w-[220px] h-12 px-1 bg-[#0BA6C7] hover:bg-[#0A99B9] text-white text-sm font-medium border border-[#0BA6C7] hover:border-[#0A99B9] rounded-xl transition-colors duration-200 active:bg-[#0991A8] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0BA6C7] pointer-events-auto"
          >
            <Icon className="w-6 h-6" />
            Create Playlist
          </button>
        </div>
      </div>

      {/* Create Playlist Popup */}
      {showCreateForm && (
        <div className="relative z-50">
          <ItemListPopup
            title="Create playlist"
            onClose={handleCloseCreateForm}
          >
            <CreatePlaylistForm
              onClose={handleCloseCreateForm}
              onSubmit={handleCreatePlaylist}
              isLoading={isCreating}
            />
          </ItemListPopup>
        </div>
      )}
    </>
  );
};

export default CreatePlaylistScreen;
